import { ChevronLeft, ChevronRight, type LucideIcon } from "lucide-react";
// biome-ignore lint/style/useImportType: Classic JSX runtime requires React.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

type FindingTone = "delay" | "attention" | "variation" | "info" | "neutral";
export type ProcessFinding = {
  id: string;
  tone: FindingTone;
  icon: LucideIcon;
  title: string;
  body: string;
  context?: string;
  action?: React.ReactNode;
};

const FINDING_STYLE: Record<FindingTone, { card: string; icon: string }> = {
  delay: { card: "border-danger/20 bg-danger/5", icon: "bg-danger/10 text-danger-strong" },
  attention: {
    card: "border-warning/20 bg-attention-soft",
    icon: "bg-warning/10 text-warning-strong",
  },
  variation: {
    card: "border-variation/20 bg-variation-soft",
    icon: "bg-variation/10 text-variation",
  },
  info: { card: "border-info/20 bg-info/5", icon: "bg-info/10 text-info-strong" },
  neutral: { card: "border-insight-line bg-insight-soft", icon: "bg-insight/10 text-insight" },
};

export default function ProcessFindingsCarousel({ findings }: { findings: ProcessFinding[] }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const activeIndex = Math.min(index, findings.length - 1);
  const active = findings[activeIndex];
  if (!active) return null;
  const pagerClass =
    "inline-flex h-7 w-7 items-center justify-center rounded text-text-mid hover:bg-surface-strong disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-info/30";
  return (
    <section
      aria-label={t("processPanel.caseFindings")}
      className={`rounded-lg border px-3 py-3 ${FINDING_STYLE[active.tone].card}`}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text">{t("processPanel.caseFindings")}</h3>
        {findings.length > 1 && (
          <div className="flex shrink-0 items-center gap-1 text-xs tabular-nums text-text-dim">
            <button
              type="button"
              aria-label={t("processPanel.noticedPrevious")}
              disabled={activeIndex === 0}
              onClick={() => setIndex(activeIndex - 1)}
              className={pagerClass}
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <span>
              {t("processPanel.noticedPosition", {
                current: activeIndex + 1,
                total: findings.length,
              })}
            </span>
            <button
              type="button"
              aria-label={t("processPanel.noticedNext")}
              disabled={activeIndex === findings.length - 1}
              onClick={() => setIndex(activeIndex + 1)}
              className={pagerClass}
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      {/* Stack text slides so paging preserves height without clipping longer findings. */}
      <div className="mt-2 grid" aria-live="polite" aria-atomic="true">
        {findings.map((finding, findingIndex) => {
          const Icon = finding.icon;
          return (
            <div
              key={finding.id}
              aria-hidden={findingIndex !== activeIndex}
              className={`col-start-1 row-start-1 flex items-start gap-3 ${findingIndex !== activeIndex ? "invisible" : ""}`}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${FINDING_STYLE[finding.tone].icon}`}
              >
                <Icon className="h-4.5 w-4.5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text">{finding.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-text-mid">{finding.body}</p>
                {finding.context && (
                  <p className="mt-0.5 text-xs leading-relaxed text-text-dim">{finding.context}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div key={active.id} className="mt-2 flex min-h-7 items-center pl-12">
        {active.action}
      </div>
    </section>
  );
}
